// Parsing for the कारक tool: the engine's sentence checker plus the labels the tool shows for each word.
// checkSentence reads a neuter word such as पोत्थअं (प्रथमा = द्वितीया) as a second कर्ता; when another word is
// the unambiguous कर्ता, that reading is shown as कर्म instead. Everything else comes straight from the checker.

import type { Analysis } from '@/prakrit/lib/grammar/analyser'
import type { Issue, Role } from '@/prakrit/lib/grammar/sentence'
import type { Case, Lang } from '@/prakrit/lib/grammar/types'

import { analyse } from '@/prakrit/lib/grammar/analyser'
import { checkSentence } from '@/prakrit/lib/grammar/sentence'

import {
    CASE_LABEL,
    GENDER_LABEL,
    KARAK,
    KRIDANTA_LABEL,
    NUMBER_LABEL,
    PERSON_LABEL,
    TENSE_LABEL
} from '@/prakrit/lib/grammar/labels'

export interface ParsedToken {
    token: string
    /** null when the sentence could not be read as a whole (an unknown word). */
    role: Role | null
    chosen: Analysis | null
    analyses: Analysis[]
}

export interface Parse {
    ok: boolean
    /** The first failing rule, if any. */
    issue: Issue | null
    notes: Issue[]
    tokens: ParsedToken[]
    /** Every word was found in the engine's tables. */
    complete: boolean
}

const hasCase = (a: Analysis, c: Case): boolean => (a.pos === 'noun' || a.pos === 'pronoun') && a.case === c

export const parseSentence = (words: string[]): Parse => {
    const result = checkSentence(words)
    const complete = result.tokens.length === words.length && result.tokens.every(t => t.analyses.length > 0)

    const tokens: ParsedToken[] = words.map((word, i) => {
        const t = result.tokens[i]

        if (!complete || !t) {
            const analyses = t?.analyses.length ? t.analyses : analyse(word)

            return { token: t?.token ?? word, role: null, chosen: analyses[0] ?? null, analyses }
        }

        // No role when the checker stopped early (for example: the sentence has no verb).
        return { token: t.token, role: t.role ?? null, chosen: t.chosen ?? t.analyses[0] ?? null, analyses: t.analyses }
    })

    if (complete) {
        const subjects = tokens.map((t, i) => (t.role === 'subject' ? i : -1)).filter(i => i >= 0)

        if (subjects.length > 1) {
            const clear = subjects.filter(i => !tokens[i].analyses.some(a => hasCase(a, 'dvitiya')))
            const keep = clear.length > 0 ? clear : [subjects[0]]

            for (const i of subjects) {
                if (keep.includes(i)) continue

                const accusative = tokens[i].analyses.find(a => hasCase(a, 'dvitiya'))

                if (accusative) tokens[i] = { ...tokens[i], role: 'object', chosen: accusative }
            }
        }
    }

    return {
        ok: result.ok,
        issue: result.issues[0] ?? null,
        notes: result.notes,
        tokens,
        complete
    }
}

export interface RoleInfo {
    hi: string
    en: string
    /** One plain line for Kids mode. */
    kids: Lang
    tone: string
}

export const ROLE_INFO: Record<Role, RoleInfo> = {
    verb: {
        hi: 'क्रिया',
        en: 'verb',
        kids: { hi: 'काम बताने वाला शब्द', en: 'the action word' },
        tone: 'border-saffron/50 bg-saffron/12 text-saffron-strong'
    },
    subject: {
        hi: 'कर्ता',
        en: 'subject (the doer)',
        kids: { hi: 'काम करने वाला', en: 'who does it' },
        tone: 'border-prakrit/35 bg-prakrit/8 text-prakrit'
    },
    object: {
        hi: 'कर्म',
        en: 'object',
        kids: { hi: 'जिस पर काम होता है', en: 'what the action is done to' },
        tone: 'border-terracotta/45 bg-terracotta/10 text-terracotta'
    },
    adverb: {
        hi: 'अव्यय',
        en: 'indeclinable',
        kids: { hi: 'कब, कैसे या कहाँ बताने वाला शब्द', en: 'tells when, how or where' },
        tone: 'border-success/40 bg-success-soft text-success'
    },
    predicate: {
        hi: 'विधेय',
        en: 'complement',
        kids: { hi: 'कर्ता के बारे में बताने वाला शब्द', en: 'says something about the doer' },
        tone: 'border-prakrit/30 bg-secondary text-prakrit'
    },
    other: {
        hi: 'अन्य',
        en: 'other role',
        kids: { hi: 'वाक्य में दूसरी भूमिका', en: 'another role in the sentence' },
        tone: 'border-border bg-secondary text-muted-foreground'
    }
}

/** The कारक of a noun or pronoun reading (for words whose role is "अन्य"). */
export const karakOf = (a: Analysis | null): { case: Case, karak: Lang } | null => {
    if (!a || (a.pos !== 'noun' && a.pos !== 'pronoun')) return null

    return { case: a.case, karak: KARAK[a.case] }
}

/** Where the full table of a word's head lives in the Grammar Lab (null for अव्यय, numbers and irregular forms). */
export const tableHref = (a: Analysis | null): string | null => {
    if (!a) return null

    switch (a.pos) {
        case 'verb':
            return `/grammar/kriya?root=${encodeURIComponent(a.lemma)}&tense=${a.tense}`
        case 'as':
            return `/grammar/kriya?root=${encodeURIComponent('अस')}&tense=${a.tense}`
        case 'dhatu':
            return `/grammar/kriya?root=${encodeURIComponent(a.lemma)}`
        case 'kridanta':
            return `/grammar/kridanta?root=${encodeURIComponent(a.lemma)}`
        case 'noun':
        case 'stem':
            return `/grammar/shabdarup?stem=${encodeURIComponent(a.lemma)}`
        case 'pronoun':
            return `/grammar/sarvanam?table=${encodeURIComponent(a.slug)}`
        default:
            return null
    }
}

export interface AnalysisView {
    tags: Lang[]
    /** The dictionary head: root, stem or word. */
    lemma: string
    lemmaKind: Lang
    meaning: Lang
    note?: Lang
}

const lang = (hi: string, en: string): Lang => ({ hi, en })

/** Grammar tags, head word and meaning for one reading, all from the engine's labels. */
export const describeAnalysis = (a: Analysis, all: Analysis[] = []): AnalysisView => {
    const meaning = lang(a.hi, a.en)

    switch (a.pos) {
        case 'verb':
            return {
                tags: [TENSE_LABEL[a.tense], PERSON_LABEL[a.person], NUMBER_LABEL[a.number]],
                lemma: a.lemma,
                lemmaKind: lang('धातु', 'root'),
                meaning,
                note: a.status === 'alt' ? lang('विकल्प रूप — सही माना जाता है', 'a variant form, accepted as correct') : undefined
            }
        case 'as':
            return {
                tags: [TENSE_LABEL[a.tense], PERSON_LABEL[a.person], NUMBER_LABEL[a.number]],
                lemma: a.lemma,
                lemmaKind: lang('धातु', 'root'),
                meaning,
                note: a.negative ? lang('निषेध: ण + अत्थि', 'negative: ण + अत्थि') : undefined
            }
        case 'noun': {
            const shared = (a.case === 'chaturthi' || a.case === 'shashthi')
                && all.some(o => o.pos === 'noun' && o.lemma === a.lemma && o.number === a.number && o.case === (a.case === 'chaturthi' ? 'shashthi' : 'chaturthi'))

            return {
                tags: [CASE_LABEL[a.case], NUMBER_LABEL[a.number], GENDER_LABEL[a.gender]],
                lemma: a.lemma,
                lemmaKind: lang('शब्द', 'stem'),
                meaning,
                note: shared ? lang('चतुर्थी और षष्ठी के रूप एक जैसे हैं (नि.४६)', 'चतुर्थी and षष्ठी share their forms (नि.४६)') : undefined
            }
        }
        case 'pronoun': {
            const shared = (a.case === 'chaturthi' || a.case === 'shashthi')
                && all.some(o => o.pos === 'pronoun' && o.lemma === a.lemma && o.number === a.number && o.case === (a.case === 'chaturthi' ? 'shashthi' : 'chaturthi'))

            return {
                tags: [CASE_LABEL[a.case], NUMBER_LABEL[a.number], PERSON_LABEL[a.person]],
                lemma: a.base,
                lemmaKind: lang('सर्वनाम', 'pronoun'),
                meaning,
                note: shared ? lang('चतुर्थी और षष्ठी के रूप एक जैसे हैं (नि.४६)', 'चतुर्थी and षष्ठी share their forms (नि.४६)') : undefined
            }
        }
        case 'avyaya':
            return { tags: [lang('अव्यय', 'indeclinable')], lemma: a.lemma, lemmaKind: lang('अव्यय', 'indeclinable'), meaning }
        case 'number':
            return { tags: [lang('संख्या', 'number')], lemma: a.lemma, lemmaKind: lang('संख्या', 'number'), meaning }
        case 'kridanta':
            return { tags: [KRIDANTA_LABEL[a.kind]], lemma: a.lemma, lemmaKind: lang('धातु', 'root'), meaning }
        case 'irregular':
            return {
                tags: [a.kind === 'bhoot_kridanta' ? lang('भूत कृदन्त (अनियमित)', 'past participle (irregular)') : lang('सम्बन्ध कृदन्त (अनियमित)', 'gerund (irregular)')],
                lemma: a.root ?? a.lemma,
                lemmaKind: a.root ? lang('धातु', 'root') : lang('रूप', 'form'),
                meaning
            }
        case 'dhatu':
            return { tags: [lang('मूल धातु', 'bare root')], lemma: a.lemma, lemmaKind: lang('धातु', 'root'), meaning }
        case 'stem':
            return { tags: [lang('मूल शब्द', 'bare stem'), GENDER_LABEL[a.gender]], lemma: a.lemma, lemmaKind: lang('शब्द', 'stem'), meaning }
    }
}
